import React from 'react';
import { RadioGroup, Radio } from '@cad-arc';
import { CommonControlProps, IntegratedFormControl } from './integrated-form-control';
import { FieldPath, FieldValues } from 'react-hook-form';
import { mapInputSizeToCommonSize } from './utils';

type Option = {
    label: string;
    value: string | number;
};

type RadioGroupProps = React.ComponentProps<typeof RadioGroup>;
type Props<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = CommonControlProps<TFieldValues, TName> & {
    radioGroupProps?: Partial<RadioGroupProps>;
    defaultValue?: string | number | undefined;
    items: Option[];
};

const renderViewMode = (value: Option[]) => {
    return value.map((option) => option.label).join(', ');
}

export function FormRadioGroup<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(props: Props<TFieldValues, TName>): React.ReactElement {
    const { radioGroupProps, ...integratedFormControlProps } = props;
    const radioGroupPropsMemo = React.useMemo(() => radioGroupProps ?? {}, [radioGroupProps]);

    return (
        <IntegratedFormControl
            {...integratedFormControlProps}
            renderViewMode={renderViewMode}
            showLabel={false} // If showLabel is true, a small amount of extra height is added to the checkbox container and ruins alignment
        >
            {(inputProps) => {
                return (
                    <RadioGroup {...mapInputSizeToCommonSize(inputProps)} {...radioGroupPropsMemo}>
                        {props.items.map((item) => (
                            <Radio key={item.label} value={item.value}>
                                {item.label}
                            </Radio>
                        ))}
                    </RadioGroup>
                );
            }}
        </IntegratedFormControl>
    );
}
